import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CMSArticleSection } from '../components/CMSArticleSection';
import { Button } from '../components/ui/button';
import { ArrowLeft, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useT } from '../i18n/I18nProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectPage = () => {
  const { slug } = useParams();
  const t = useT();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/content/projects-full/${slug}`)
      .then((r) => setProject(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        {t('common.loading')}
      </div>
    );
  if (err || !project)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-2xl font-bold text-[#0A1628]">{t('project.not_found')}</h1>
        <Link to="/" className="text-[#1565C0] hover:underline">{t('project.back_home')}</Link>
      </div>
    );

  return (
    <div className="min-h-screen bg-white" data-testid={`project-page-${slug}`}>
      <Header />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0"
             style={{ backgroundImage: `url(${project.hero_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-4 py-24 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6" data-testid="project-back">
            <ArrowLeft className="w-4 h-4" /> {t('about.back_to_home')}
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-black mb-3">
            {t('project.eyebrow')}
          </div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">{project.title}</h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl">{project.tagline}</p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24 max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2 prose prose-lg max-w-none text-gray-700 prose-strong:text-[#1565C0]"
                   dangerouslySetInnerHTML={{ __html: project.description || '' }} />
          <aside className="space-y-6">
            {Array.isArray(project.highlights) && project.highlights.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100 p-6 shadow-sm">
                <h3 className="text-xs uppercase tracking-widest text-[#1565C0] font-black mb-4">
                  {t('project.highlights')}
                </h3>
                <ul className="space-y-3 text-sm">
                  {project.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {project.cta_label && project.cta_link && (
              <a href={project.cta_link} target="_blank" rel="noreferrer noopener" className="block">
                <Button
                  className="w-full bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white font-black uppercase tracking-wider text-sm py-6 rounded-full shadow-xl hover:shadow-blue-500/50"
                  data-testid="project-cta"
                >
                  {project.cta_label} <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            )}
          </aside>
        </div>

        {Array.isArray(project.gallery) && project.gallery.length > 0 && (
          <div className="mt-16">
            <h3 className="text-xs uppercase tracking-widest text-[#1565C0] font-black mb-4">{t('project.gallery')}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {project.gallery.map((url, i) => (
                <img key={i} src={url} alt={`${project.title} photo ${i+1}`}
                     className="w-full h-72 object-cover rounded-2xl shadow-lg" />
              ))}
            </div>
          </div>
        )}
      </section>
      {/* Research Library — only on the Research project page */}
      {slug === 'research' && (
        <CMSArticleSection
          anchor="research-papers"
          slug="research-papers"
          eyebrow={t('research.eyebrow')}
          title={t('research.title')}
          accent={t('research.title_accent')}
          subtitle={t('research.subtitle')}
          variant="pdf"
        />
      )}
      <Footer />
    </div>
  );
};

export default ProjectPage;
